import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CustomTextField } from '../Textfield';
import { connect, useDispatch } from 'react-redux';
import {
    createBDStaffAdmin,
    createBluedartInternalUser,
    editBDStaffAdmin,
} from '../../store/actions/staffActionCreator';
import { Add } from '@material-ui/icons';
import { showMessage } from '../../utils/helper';

// ---- Types ----

export interface AdminUserFormValues {
    id?: number | null;
    fullName: string;
    email: string;
    mobile: string;
    role: string;
}

export type AdminUserMode = 'create' | 'edit';

export interface AdminUserDialogProps {
    open: boolean;
    mode?: AdminUserMode;
    initialData?: Partial<AdminUserFormValues> | null;
    onClose?: () => void;
    onSuccess?: () => void;
    [key: string]: any;
}

// ---- Role List Constant ----

export const ADMIN_ROLE_OPTIONS: { label: string; value: string }[] = [
    { label: 'Super Admin', value: 'BLUEDART_SUPER_ADMIN' },
    { label: 'Regional Approver', value: 'BLUEDART_ZONAL_APPROVER' },
    // Role 31 keeps its historical backend name; only the label changed.
    { label: 'Rider Approver', value: 'BLUEDART_RIDER_APPROVER' },
    { label: 'Rider Viewer', value: 'BLUEDART_RIDER_VIEWER' },
    // Not one of the PRD's four internal roles. Added so a second login can be attached to an
    // existing vendor org — vendor creation auto-creates exactly one, and nothing else exposed a
    // way to add more. Scoped by organisation, never by stores or zones.
    { label: 'Vendor', value: 'BLUEDART_VENDOR' },
];

/** RB-2: which scope selector the chosen role gets. Super Admin is global and gets none. */
export const scopeTypeForRole = (
    role?: string,
): 'STORE' | 'ZONE' | 'GLOBAL' | 'ORG' => {
    // A vendor login is scoped by organisation, not by stores or zones.
    if (role === 'BLUEDART_VENDOR') return 'ORG';
    if (role === 'BLUEDART_ZONAL_APPROVER') return 'ZONE';
    if (role === 'BLUEDART_RIDER_APPROVER' || role === 'BLUEDART_RIDER_VIEWER')
        return 'STORE';
    return 'GLOBAL';
};

// ---- Validation Schema ----

const phoneRegExp = /^[0-9]{10}$/;

const validationSchema: yup.SchemaOf<AdminUserFormValues> = yup.object({
    id: yup.number().nullable().optional(),
    fullName: yup.string().trim().required('User Full Name is required'),
    email: yup
        .string()
        .trim()
        .email('Enter a valid email')
        .required('Official Email ID is required'),
    mobile: yup
        .string()
        .matches(phoneRegExp, 'Mobile Number must be 10 digits')
        .required('Mobile Number is required'),
    role: yup
        .string()
        .oneOf(
            ADMIN_ROLE_OPTIONS.map((r) => r.value),
            'Select a valid role',
        )
        .required('User Role is required'),
});

// ---- Component ----

const AdminUserModal: React.FC<AdminUserDialogProps> = ({
    open,
    mode = 'create',
    initialData,
    onClose,
    onSuccess,
    // Supplied by the User Management tab, already narrowed server-side to what this creator may
    // grant. Absent when opened from the navbar, where only the unscoped roles are offered.
    assignableRoles,
    stores,
    zones,
    vendorOrgs,
}) => {
    const dispatch = useDispatch();
    const dispatchCreateStaffAdmin = (
            data: any,
            onSuccess: (resp: any) => void,
            onError: (err: any) => void,
        ) => dispatch(createBDStaffAdmin(data, onSuccess, onError)),
        dispatchEditStaffAdmin = (
            data: any,
            onSuccess: (resp: any) => void,
            onError: (err: any) => void,
        ) => dispatch(editBDStaffAdmin(data, onSuccess, onError)),
        dispatchCreateInternalUser = (
            data: any,
            onSuccess: (resp: any) => void,
            onError: (err: any) => void,
        ) => dispatch(createBluedartInternalUser(data, onSuccess, onError));
    const [submitting, setSubmitting] = useState(false);
    const [selectedScope, setSelectedScope] = useState<string[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string>('');
    const [scopeQuery, setScopeQuery] = useState<string>('');
    const [serverError, setServerError] = useState<string>('');

    const isEdit = mode === 'edit';

    const initialValues: AdminUserFormValues = useMemo(
        () => ({
            id: initialData?.id ?? null,
            fullName: initialData?.fullName ?? '',
            email: initialData?.email ?? '',
            mobile: initialData?.mobile ?? '',
            role: initialData?.role ?? '',
        }),
        [initialData],
    );

    const formik = useFormik<AdminUserFormValues>({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            if (!dispatchCreateStaffAdmin || !dispatchEditStaffAdmin) return;

            setSubmitting(true);
            setServerError('');

            const payload = {
                name: values.fullName,
                contactNumber: values.mobile,
                email: values.email,
                role: values.role,
            };

            const handleSuccess = async () => {
                setSubmitting(false);
                showMessage(
                    'User has been successfully ' +
                        (isEdit ? 'updated' : 'created'),
                    'success',
                );
                if (onSuccess) onSuccess();
                if (onClose) onClose();
            };

            // The backend refuses duplicates and bad scope by returning the reason in
            // status.message. Shown in the dialog rather than only as a toast, because the toast
            // appears behind the open modal and the user is looking at the form they just
            // submitted.
            const handleError = async (error: any) => {
                setSubmitting(false);
                setServerError(
                    error?.status?.message ||
                        (typeof error === 'string' ? error : '') ||
                        'Could not save this user. Please try again.',
                );
            };

            if (isEdit && values.id) {
                dispatchEditStaffAdmin(
                    {
                        id: values.id,
                        value: payload,
                    },
                    handleSuccess,
                    handleError,
                );
            } else {
                // Created through /bluedart/internal-users, not the legacy admin endpoint: it is
                // the only path that writes bluedart_user_scope rows and validates the creation
                // hierarchy. A scoped role created without scope is born unable to see anything.
                dispatchCreateInternalUser(
                    scopeSelectorType === 'ORG'
                        ? { ...payload, organizationId: selectedOrgId }
                        : { ...payload, scopeValues: selectedScope },
                    handleSuccess,
                    handleError,
                );
            }
        },
    });

    // Derived from the live form value so the selector switches as soon as the role changes.
    const scopeSelectorType = scopeTypeForRole(formik.values.role);
    const scopeOptions = useMemo(() => {
        if (scopeSelectorType === 'ZONE') {
            return (zones ?? []).map((z: string) => ({
                value: z,
                label: z,
                search: (z ?? '').toLowerCase(),
            }));
        }
        if (scopeSelectorType === 'STORE') {
            return (stores ?? []).map((st: any) => ({
                value: st.storeId,
                label: `${st.storeId}${st.storeName ? ` — ${st.storeName}` : ''}`,
                // City and zone are searchable but stay out of the label, which has to read as the
                // store id the approver knows.
                search: `${st.storeId ?? ''} ${st.storeName ?? ''} ${st.city ?? ''} ${
                    st.zone ?? ''
                }`.toLowerCase(),
            }));
        }
        return [];
    }, [scopeSelectorType, zones, stores]);

    // Prod carries ~2,100 stores and the endpoint returns them all in one call, so the list is
    // filtered here rather than server-side. Capped because rendering every checkbox is slow
    // enough to be felt on open. Anything already ticked stays visible as a chip above, which is
    // what makes a selection removable even when the current filter hides it.
    const SCOPE_RENDER_LIMIT = 200;
    const filteredScopeOptions = useMemo(() => {
        const q = scopeQuery.trim().toLowerCase();
        if (!q) {
            return scopeOptions;
        }
        return scopeOptions.filter((o: any) => o.search.includes(q));
    }, [scopeOptions, scopeQuery]);
    const visibleScopeOptions = filteredScopeOptions.slice(
        0,
        SCOPE_RENDER_LIMIT,
    );

    // Switching between a store-scoped and a zone-scoped role must not carry the previous
    // selection over — store ids in a zone field would be rejected server-side.
    useEffect(() => {
        setSelectedScope([]);
        setSelectedOrgId('');
        setScopeQuery('');
    }, [scopeSelectorType]);

    useEffect(() => {
        if (!open) {
            setServerError('');
            formik.resetForm();
            setSubmitting(false);
            setScopeQuery('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleClose = () => {
        if (submitting) return;
        if (onClose) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="admin-user-dialog-title"
            disableBackdropClick
        >
            <DialogTitle id="admin-user-dialog-title">
                {isEdit ? 'Edit Internal User' : 'Create Internal User'}
            </DialogTitle>
            <DialogContent>
                <Box mt={1}>
                    <Typography variant="subtitle2" gutterBottom>
                        User Full Name
                    </Typography>
                    <CustomTextField
                        fullWidth
                        name="fullName"
                        placeholder="John Doe"
                        value={formik.values.fullName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.fullName &&
                            Boolean(formik.errors.fullName)
                        }
                        helperText={
                            formik.touched.fullName && formik.errors.fullName
                        }
                    />
                </Box>

                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Official Email ID
                    </Typography>
                    <CustomTextField
                        fullWidth
                        name="email"
                        placeholder="john@loadshare.net"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                    />
                </Box>

                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Mobile Number
                    </Typography>
                    <CustomTextField
                        fullWidth
                        name="mobile"
                        placeholder="+91 0000000000"
                        value={formik.values.mobile}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.mobile &&
                            Boolean(formik.errors.mobile)
                        }
                        helperText={
                            formik.touched.mobile && formik.errors.mobile
                        }
                    />
                </Box>

                <Box mt={2} mb={1}>
                    <Typography variant="subtitle2" gutterBottom>
                        Select User Role &amp; Permissions
                    </Typography>
                    <CustomTextField
                        select
                        fullWidth
                        name="role"
                        placeholder="Select Role"
                        value={formik.values.role}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.role && Boolean(formik.errors.role)
                        }
                        helperText={formik.touched.role && formik.errors.role}
                    >
                        {ADMIN_ROLE_OPTIONS.filter(
                            (role) =>
                                !assignableRoles?.length ||
                                assignableRoles.includes(role.value),
                        ).map((role) => (
                            <MenuItem key={role.value} value={role.value}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </CustomTextField>

                    {/* RB-2: store multi-select for Rider Approver / Viewer, zone multi-select for
                        Regional Approver, nothing for Super Admin. Mandatory for the scoped roles —
                        a scoped user created with no scope can log in and see nothing. */}
                    {scopeSelectorType === 'ORG' && (
                        <div style={{ marginTop: 12 }}>
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    marginBottom: 6,
                                }}
                            >
                                Vendor Organisation
                                <span style={{ color: '#c62828' }}> *</span>
                            </div>
                            <Autocomplete
                                options={vendorOrgs ?? []}
                                getOptionLabel={(org: any) =>
                                    `${org?.name ?? ''}${
                                        org?.vendorCode
                                            ? ` (${org.vendorCode})`
                                            : ''
                                    }`
                                }
                                getOptionSelected={(org: any, picked: any) =>
                                    org?.orgId === picked?.orgId
                                }
                                value={
                                    (vendorOrgs ?? []).find(
                                        (org: any) =>
                                            String(org?.orgId) ===
                                            String(selectedOrgId),
                                    ) ?? null
                                }
                                onChange={(_e: any, picked: any) =>
                                    // Kept exactly as the API returned it — the payload sends
                                    // organizationId straight through.
                                    setSelectedOrgId(picked?.orgId ?? '')
                                }
                                renderInput={(params: any) => (
                                    <CustomTextField
                                        {...params}
                                        fullWidth
                                        name="organizationId"
                                        placeholder="Search vendor organisation"
                                        helperText={
                                            selectedOrgId
                                                ? ''
                                                : 'Select the vendor this login belongs to'
                                        }
                                    />
                                )}
                            />
                        </div>
                    )}

                    {scopeSelectorType !== 'GLOBAL' &&
                        scopeSelectorType !== 'ORG' && (
                            <div style={{ marginTop: 12 }}>
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        marginBottom: 6,
                                    }}
                                >
                                    {scopeSelectorType === 'ZONE'
                                        ? 'Zones'
                                        : 'Store IDs'}
                                    <span style={{ color: '#c62828' }}> *</span>
                                </div>
                                <div style={{ marginBottom: 6 }}>
                                    {selectedScope.map((value) => (
                                        <Chip
                                            key={value}
                                            size="small"
                                            label={value}
                                            onDelete={() =>
                                                setSelectedScope((prev) =>
                                                    prev.filter(
                                                        (v) => v !== value,
                                                    ),
                                                )
                                            }
                                            style={{
                                                marginRight: 4,
                                                marginBottom: 4,
                                            }}
                                        />
                                    ))}
                                </div>
                                <CustomTextField
                                    fullWidth
                                    value={scopeQuery}
                                    onChange={(e: any) =>
                                        setScopeQuery(e.target.value)
                                    }
                                    placeholder={
                                        scopeSelectorType === 'ZONE'
                                            ? 'Search zones'
                                            : 'Search by store ID, name or city'
                                    }
                                />
                                <div
                                    style={{
                                        maxHeight: 180,
                                        overflow: 'auto',
                                        border: '1px solid #e3e6ea',
                                        borderRadius: 4,
                                        padding: 8,
                                        marginTop: 6,
                                    }}
                                >
                                    {visibleScopeOptions.map(
                                        (opt: {
                                            value: string;
                                            label: string;
                                        }) => (
                                            <FormControlLabel
                                                key={opt.value}
                                                style={{ display: 'flex' }}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedScope.includes(
                                                            opt.value,
                                                        )}
                                                        onChange={() =>
                                                            setSelectedScope(
                                                                (prev) =>
                                                                    prev.includes(
                                                                        opt.value,
                                                                    )
                                                                        ? prev.filter(
                                                                              (
                                                                                  v,
                                                                              ) =>
                                                                                  v !==
                                                                                  opt.value,
                                                                          )
                                                                        : [
                                                                              ...prev,
                                                                              opt.value,
                                                                          ],
                                                            )
                                                        }
                                                    />
                                                }
                                                label={opt.label}
                                            />
                                        ),
                                    )}
                                    {filteredScopeOptions.length === 0 && (
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: '#6b7280',
                                            }}
                                        >
                                            {scopeQuery.trim()
                                                ? 'No matches.'
                                                : 'No options available.'}
                                        </span>
                                    )}
                                    {filteredScopeOptions.length >
                                        SCOPE_RENDER_LIMIT && (
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: '#6b7280',
                                            }}
                                        >
                                            Showing {SCOPE_RENDER_LIMIT} of{' '}
                                            {filteredScopeOptions.length}. Keep
                                            typing to narrow the list.
                                        </span>
                                    )}
                                </div>
                                {selectedScope.length === 0 && (
                                    <div
                                        style={{
                                            color: '#c62828',
                                            fontSize: 12,
                                            marginTop: 6,
                                        }}
                                    >
                                        Select at least one{' '}
                                        {scopeSelectorType === 'ZONE'
                                            ? 'zone'
                                            : 'store'}
                                        .
                                    </div>
                                )}
                            </div>
                        )}
                </Box>
                {!!serverError && (
                    <Box mt={1}>
                        <Typography
                            variant="body2"
                            style={{ color: '#d32f2f', fontWeight: 500 }}
                        >
                            {serverError}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color="default"
                    variant="contained"
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={formik.submitForm}
                    disabled={
                        !formik.isValid ||
                        submitting ||
                        (!isEdit &&
                            scopeSelectorType === 'ORG' &&
                            !selectedOrgId) ||
                        (!isEdit &&
                            scopeSelectorType !== 'GLOBAL' &&
                            scopeSelectorType !== 'ORG' &&
                            selectedScope.length === 0)
                    }
                >
                    {submitting && (
                        <CircularProgress
                            size={18}
                            style={{ marginRight: 8, color: 'inherit' }}
                        />
                    )}
                    {isEdit ? 'Save Changes' : 'Create User'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminUserModal;
